import { useEffect, useRef } from 'react';

import { useLicense } from '@cloud-ui/hooks/useLicense';
import { LicenseStatusEnum } from '@shared/dto/config';

import { TriangleAlert } from 'lucide-react';

const DEFAULT_NAV_HEIGHT = 66;

export function LicenseBanner() {
  const { licenseInfo, isLoading } = useLicense();
  const bannerRef = useRef<HTMLDivElement>(null);

  const isVisible =
    !isLoading &&
    !licenseInfo.isValid &&
    !!licenseInfo.licenseStatus &&
    licenseInfo.licenseStatus.status !== LicenseStatusEnum.NO_LICENSE;

  useEffect(() => {
    if (!isVisible) {
      document.documentElement.style.setProperty('--navigation-height', `${DEFAULT_NAV_HEIGHT}px`);
      return;
    }

    const updateHeight = () => {
      const bannerHeight = bannerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        '--navigation-height',
        `${DEFAULT_NAV_HEIGHT + bannerHeight}px`,
      );
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--navigation-height', `${DEFAULT_NAV_HEIGHT}px`);
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const getMessage = () => {
    const statusMessage = licenseInfo.getStatusMessage();
    const cutOffMessage = licenseInfo.isExpired ? licenseInfo.getCutOffMessage() : null;

    if (cutOffMessage) {
      return `${statusMessage}. ${cutOffMessage}`;
    }
    return statusMessage;
  };

  return (
    <div
      ref={bannerRef}
      data-testid="license-banner"
      className="fixed right-0 left-0 z-(--z-banner) flex flex-wrap items-center justify-center gap-2 bg-destructive px-4 py-2 text-center text-destructive-foreground text-sm shadow-sm"
      style={{ top: `${DEFAULT_NAV_HEIGHT}px` }}
    >
      <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
      <span className="max-w-full break-words font-medium leading-6">{getMessage()}</span>
    </div>
  );
}
